import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import KuikaTooltip from "../../../../ui-kit/kuika-tooltip/kuika-tooltip";
import type { SearchState } from "../shared/mcp-chat-types";

interface McpChatSearchBarProps {
  searchState: SearchState;
  onQueryChange: (query: string) => void;
  onNextMatch: () => void;
  onPrevMatch: () => void;
  onToggleCrossTab: () => void;
  onClose: () => void;
}

const McpChatSearchBar: React.FC<McpChatSearchBarProps> = ({
  searchState,
  onQueryChange,
  onNextMatch,
  onPrevMatch,
  onToggleCrossTab,
  onClose
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [localValue, setLocalValue] = useState(searchState.query);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    setLocalValue(searchState.query);
  }, [searchState.query]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setLocalValue(val);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => onQueryChange(val), 150);
    },
    [onQueryChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        if (e.shiftKey) {
          onPrevMatch();
        } else {
          onNextMatch();
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    },
    [onNextMatch, onPrevMatch, onClose]
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const { matches, currentMatchIndex } = searchState;
  const hasMatches = matches.length > 0;
  const matchLabel =
    searchState.query.length >= 2 ? (hasMatches ? `${currentMatchIndex + 1} of ${matches.length}` : "No results") : "";

  return (
    <div className="mcp-chat__search-bar">
      <svg
        className="mcp-chat__search-bar-icon"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
      <input
        ref={inputRef}
        className="mcp-chat__search-input"
        type="text"
        placeholder="Search messages..."
        value={localValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        spellCheck={false}
        autoComplete="off"
      />
      {matchLabel && <span className="mcp-chat__search-count">{matchLabel}</span>}
      <button
        className="mcp-chat__search-btn"
        onClick={onPrevMatch}
        disabled={!hasMatches}
        aria-label="Previous match"
        title="Previous match (Shift+Enter)"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="18 15 12 9 6 15" />
        </svg>
      </button>
      <button
        className="mcp-chat__search-btn"
        onClick={onNextMatch}
        disabled={!hasMatches}
        aria-label="Next match"
        title="Next match (Enter)"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      <span className="mcp-chat__search-divider" />
      <KuikaTooltip content={searchState.crossTab ? "Search current tab only" : "Search all tabs"} position="bottom">
        <button
          className={`mcp-chat__search-btn${searchState.crossTab ? " mcp-chat__search-btn--active" : ""}`}
          onClick={onToggleCrossTab}
          aria-label={searchState.crossTab ? "Search current tab only" : "Search all tabs"}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="3" width="7" height="7" />
            <rect x="14" y="3" width="7" height="7" />
            <rect x="3" y="14" width="7" height="7" />
            <rect x="14" y="14" width="7" height="7" />
          </svg>
        </button>
      </KuikaTooltip>
      <button className="mcp-chat__search-btn" onClick={onClose} aria-label="Close search" title="Close search (Esc)">
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
};

export default memo(McpChatSearchBar);
